import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { getCategoryName } from "../../config/categories";
import { getProductPath } from "../../config/products";
import StatusBadge from "./StatusBadge";
import ProductGalleryPlaceholder from "./ProductGalleryPlaceholder";
import { getPrimaryProductImage } from "../../lib/productMedia";

/**
 * Shared product/variant card for catalogue, shop and related products.
 */
export default function ProductCard({ product, index = 0, mode = "catalog" }) {
  const categoryLabel = product.subcategory || getCategoryName(product.category);
  const detailTo = getProductPath(product);
  const showQuote =
    product.quoteEnabled && !product.priceVisible && mode !== "link-only";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.06, ease: [0.19, 1, 0.22, 1] }}
      className="steel-card clip-frame group flex flex-col h-full"
      data-testid={`product-card-${product.sku}`}
    >
      <Link to={detailTo} className="block border-b border-border overflow-hidden bg-secondary/20">
        {getPrimaryProductImage(product) ? (
          <img
            src={getPrimaryProductImage(product)}
            alt={product.name}
            className="w-full aspect-[4/3] object-cover object-center group-hover:scale-[1.02] transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <ProductGalleryPlaceholder compact label="Product Images" />
        )}
      </Link>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
            {categoryLabel}
          </div>
          <StatusBadge status={product.status} />
        </div>

        <Link to={detailTo} className="mt-3 block">
          <h3 className="display display-section text-xl leading-tight group-hover:text-[color:var(--gold)] transition-colors duration-500">
            {product.name}
          </h3>
        </Link>
        <div className="mt-1 font-mono-2 text-[10px] tracking-[0.15em] text-muted-foreground">
          {product.sku}
          {product.wattage ? ` · ${product.wattage}` : ""}
        </div>
        <p className="mt-3 text-sm text-steel-text leading-relaxed line-clamp-3 flex-1">
          {product.shortDescription || product.description}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {(product.applications || []).slice(0, 2).map((a) => (
            <span
              key={a}
              className="font-mono-2 text-[9px] tracking-[0.12em] uppercase px-2 py-1 border border-border text-muted-foreground"
            >
              {a}
            </span>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="font-mono-2 text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
            {product.priceVisible && product.price != null
              ? `₹${Number(product.price).toLocaleString("en-IN")}`
              : "Quote Mode"}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={detailTo}
              className="inline-flex items-center gap-1 font-mono-2 text-[11px] tracking-[0.18em] uppercase"
              style={{ color: "var(--gold)" }}
              data-testid={`product-view-${product.sku}`}
            >
              View <ArrowUpRight size={13} />
            </Link>
            {showQuote && (
              <Link
                to={`/portal?quote=${encodeURIComponent(product.sku)}`}
                className="btn-gold clip-frame-sm px-3 py-2 text-[10px] uppercase tracking-widest font-mono-2"
                data-testid={`product-quote-${product.sku}`}
              >
                Quote
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
